import { parseWithZod } from "@conform-to/zod";
import { invariantResponse } from "@epic-web/invariant";
import {
	type ActionFunctionArgs,
	type HeadersFunction,
	type LinksFunction,
	type LoaderFunctionArgs,
	type MetaFunction,
	json,
} from "@remix-run/node";
import {
	Form,
	Link,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useFetchers,
	useLoaderData,
	useSubmit,
} from "@remix-run/react";
import { withSentry } from "@sentry/remix";
import { useRef } from "react";
import { HoneypotProvider } from "remix-utils/honeypot/react";
import { z } from "zod";
import { GeneralErrorBoundary } from "./components/error-boundary.tsx";
import { EpicProgress } from "./components/progress-bar.tsx";
import { useToast } from "./components/toaster.tsx";
import { Button } from "./components/ui/button.tsx";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuPortal,
	DropdownMenuTrigger,
} from "./components/ui/dropdown-menu.tsx";
import { Icon, href as iconsHref } from "./components/ui/icon.tsx";
import { EpicToaster } from "./components/ui/sonner.tsx";
import tailwindStyleSheetUrl from "./styles/tailwind.css?url";
import { getUserId, logout } from "./utils/auth.server.ts";
import { ClientHintCheck, getHints, useHints } from "./utils/client-hints.tsx";
import { prisma } from "./utils/db.server.ts";
import { getEnv } from "./utils/env.server.ts";
import { honeypot } from "./utils/honeypot.server.ts";
import {
	combineHeaders,
	getCompanyImgSrc,
	getDomainUrl,
} from "./utils/misc.tsx";
import { useNonce } from "./utils/nonce-provider.ts";
import { useRequestInfo } from "./utils/request-info.ts";
import { type Theme, getTheme, setTheme } from "./utils/theme.server.ts";
import { makeTimings, time } from "./utils/timing.server.ts";
import { getToast } from "./utils/toast.server.ts";
import { useOptionalUser, useUser } from "./utils/user.ts";

export const links: LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: "preload", href: iconsHref, as: "image" },
		// Preload CSS as a resource to avoid render blocking
		{ rel: "preload", href: tailwindStyleSheetUrl, as: "style" },
		{ rel: "mask-icon", href: "/favicons/mask-icon.svg" },
		{
			rel: "alternate icon",
			type: "image/png",
			href: "/favicons/favicon-32x32.png",
		},
		{ rel: "apple-touch-icon", href: "/favicons/apple-touch-icon.png" },
		{
			rel: "manifest",
			href: "/site.webmanifest",
			crossOrigin: "use-credentials",
		} as const, // necessary to make typescript happy
		//These should match the css preloads above to avoid css as render blocking resource
		{ rel: "icon", type: "image/svg+xml", href: "/favicons/favicon.svg" },
		{ rel: "stylesheet", href: tailwindStyleSheetUrl },
	].filter(Boolean);
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	return [
		{ title: data ? "Útsölur" : "Error | Útsölur" },
		{ name: "description", content: "Allar útsölur á Íslandi" },
	];
};

export async function loader({ request }: LoaderFunctionArgs) {
	const timings = makeTimings("root loader");
	const userId = await time(() => getUserId(request), {
		timings,
		type: "getUserId",
		desc: "getUserId in root",
	});

	const user = userId
		? await time(
				() =>
					prisma.user.findUniqueOrThrow({
						select: {
							id: true,
							name: true,
							company: {
								select: {
									name: true,
									logo: {
										select: { id: true },
									},
								},
							},
							image: { select: { id: true } },
						},
						where: { id: userId },
					}),
				{ timings, type: "find user", desc: "find user in root" },
		  )
		: null;
	if (userId && !user) {
		console.info("something weird happened");
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: "/" });
	}
	const { toast, headers: toastHeaders } = await getToast(request);
	const honeyProps = honeypot.getInputProps();

	return json(
		{
			user,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ "Server-Timing": timings.toString() },
				toastHeaders,
			),
		},
	);
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
	const headers = {
		"Server-Timing": loaderHeaders.get("Server-Timing") ?? "",
	};
	return headers;
};

const ThemeFormSchema = z.object({
	theme: z.enum(["system", "light", "dark"]),
});

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData();
	const submission = parseWithZod(formData, {
		schema: ThemeFormSchema,
	});

	invariantResponse(submission.status === "success", "Invalid theme received");

	const { theme } = submission.value;

	const responseInit = {
		headers: { "set-cookie": setTheme(theme) },
	};
	return json({ result: submission.reply() }, responseInit);
}

function Document({
	children,
	nonce,
	theme = "light",
	env = {},
}: {
	children: React.ReactNode;
	nonce: string;
	theme?: Theme;
	env?: Record<string, string>;
}) {
	return (
		<html lang="en" className={`${theme} h-full overflow-x-hidden`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				<Links />
			</head>
			<body className="antialiased">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	);
}

function App() {
	const data = useLoaderData<typeof loader>();
	const nonce = useNonce();
	const user = useOptionalUser();
	const theme = useTheme();
	/*const matches = useMatches();*/
	/*const isOnSearchPage = matches.find((m) => m.id === "routes/users+/index");*/
	/*const searchBar = isOnSearchPage ? null : <SearchBar status="idle" />;*/
	useToast(data.toast);

	return (
		<Document nonce={nonce} theme={theme} env={data.ENV}>
			<div className="flex h-screen flex-col justify-between">
				<header className="container shadow py-4 mb-6">
					<nav className="flex flex-wrap items-center justify-between gap-4 sm:flex-nowrap md:gap-8">
						<Logo />
						{/*<div className="ml-auto hidden max-w-sm flex-1 sm:block">
							{searchBar}
						</div>*/}
						<div className="flex items-center gap-10">
							{user ? (
								<UserDropdown />
							) : (
								<Button asChild variant="default" size="default">
									<Link to="/login">Innskráning fyrirtækja</Link>
								</Button>
							)}
						</div>
						{/*<div className="block w-full sm:hidden">{searchBar}</div>*/}
					</nav>
				</header>

				<div className="flex-1 pb-40">
					<Outlet />
				</div>

				<Footer />
				{/*<div className="container flex justify-between pb-5">
					<ThemeSwitch userPreference={data.requestInfo.userPrefs.theme} />
				</div>*/}
			</div>
			<EpicToaster closeButton position="top-center" theme={theme} />
			<EpicProgress />
		</Document>
	);
}

function Footer() {
	return (
		<footer className="bg-gradient-to-tl from-yellow-500/10 via-red-500/10 to-orange-500/10 text-yellow-950 py-8">
			<div className="container mx-auto px-4">
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
					<div>
						<h4 className="text-lg text-orange-500 font-semibold mb-4">
							Útsölur á Íslandi
						</h4>
						<p className="text-sm">
							Allar útsölur og tilboð á Íslandi. Hvar eru útsölur? Þær má finna
							hér!
						</p>
					</div>
					<div>
						<h4 className="text-lg text-orange-500 font-semibold mb-4">
							Hafðu samband
						</h4>
						<p className="text-sm">Netfang: utsolur@utsolur.is</p>
					</div>
					<div>
						<h4 className="text-lg text-orange-500 font-semibold mb-4">
							Samfélagsmiðlar
						</h4>
						<div className="flex space-x-4">
							<a
                                target={"_blank"}
								href="https://www.instagram.com/utsolurisland/"
								className="text-yellow-500 hover:text-yellow-950 transition duration-300" rel="noreferrer"
							>
								<Icon name={"instagram"} size={"lg"} />
							</a>
						</div>
					</div>
				</div>
			</div>
			<div className="mt-8 pt-8 text-center">
				<p>&copy; 2024 Útsölur. Allur réttur áskilinn.</p>
			</div>
		</footer>
	);
}

function Logo() {
	return (
		<Link to="/" className="group grid leading-snug">
			<svg
				width="144"
				height="31"
				viewBox="0 0 144 31"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
			>
				<rect width="144" height="31" fill="#B91C1C" />
				<path
					d="M18.2991 27.29C15.9018 27.29 14.0361 26.5843 12.7021 25.173C11.3875 23.7617 10.7301 21.7413 10.7301 19.112V7.657H12.8181V19.228C12.8181 21.2967 13.2821 22.8337 14.2101 23.839C15.1381 24.825 16.5011 25.318 18.2991 25.318C20.0971 25.318 21.4698 24.825 22.4171 23.839C23.3645 22.8337 23.8381 21.2967 23.8381 19.228V7.657H25.8681V19.112C25.8681 20.8713 25.5685 22.36 24.9691 23.578C24.3698 24.796 23.5095 25.724 22.3881 26.362C21.2668 26.9807 19.9038 27.29 18.2991 27.29ZM17.7771 5.772L19.4301 2.437H21.5761L19.1691 5.772H17.7771ZM35.7682 27V9.629H28.9242V7.657H44.6712V9.629H37.8562V27H35.7682ZM53.1975 27.29C52.2308 27.29 51.4091 27.2223 50.7325 27.087C50.0558 26.9517 49.4468 26.7583 48.9055 26.507C48.3641 26.2557 47.7745 25.9657 47.1365 25.637V23.375C47.7551 23.897 48.5865 24.361 49.6305 24.767C50.6745 25.173 51.8925 25.376 53.2845 25.376C54.7538 25.376 55.8171 25.0667 56.4745 24.448C57.1511 23.81 57.4895 23.0077 57.4895 22.041C57.4895 21.229 57.2671 20.5813 56.8225 20.098C56.3778 19.5953 55.8075 19.1893 55.1115 18.88C54.4155 18.5513 53.6808 18.242 52.9075 17.952C52.2501 17.72 51.5928 17.4783 50.9355 17.227C50.2975 16.9563 49.7175 16.6277 49.1955 16.241C48.6735 15.835 48.2578 15.3227 47.9485 14.704C47.6391 14.066 47.4845 13.2733 47.4845 12.326C47.4845 11.804 47.5811 11.253 47.7745 10.673C47.9871 10.093 48.3158 9.55167 48.7605 9.049C49.2245 8.54633 49.8335 8.14033 50.5875 7.831C51.3608 7.52167 52.2985 7.367 53.4005 7.367C54.1545 7.367 54.7925 7.425 55.3145 7.541C55.8558 7.63767 56.3681 7.79233 56.8515 8.005C57.3348 8.19833 57.8761 8.44 58.4755 8.73V10.876C57.9728 10.586 57.4895 10.325 57.0255 10.093C56.5808 9.84167 56.0781 9.64833 55.5175 9.513C54.9761 9.35833 54.3091 9.281 53.5165 9.281C52.5691 9.281 51.8055 9.41633 51.2255 9.687C50.6648 9.95767 50.2491 10.3153 49.9785 10.76C49.7271 11.2047 49.6015 11.6783 49.6015 12.181C49.6015 12.9737 49.7755 13.602 50.1235 14.066C50.4908 14.53 50.9935 14.9167 51.6315 15.226C52.2888 15.516 53.0428 15.806 53.8935 16.096C54.5701 16.328 55.2468 16.589 55.9235 16.879C56.6001 17.1497 57.2091 17.4977 57.7505 17.923C58.3111 18.329 58.7558 18.851 59.0845 19.489C59.4325 20.1077 59.6065 20.881 59.6065 21.809C59.6065 22.679 59.4131 23.5393 59.0265 24.39C58.6398 25.2213 57.9825 25.9173 57.0545 26.478C56.1265 27.0193 54.8408 27.29 53.1975 27.29ZM71.7484 27.29C69.8344 27.29 68.2297 26.9227 66.9344 26.188C65.6584 25.434 64.6917 24.3223 64.0344 22.853C63.3964 21.3837 63.0774 19.547 63.0774 17.343C63.0774 14.0563 63.8024 11.5817 65.2524 9.919C66.7217 8.237 68.8871 7.396 71.7484 7.396C73.6817 7.396 75.2864 7.773 76.5624 8.527C77.8577 9.26167 78.8244 10.3637 79.4624 11.833C80.1197 13.3023 80.4484 15.139 80.4484 17.343C80.4484 20.6297 79.7137 23.114 78.2444 24.796C76.7944 26.4587 74.6291 27.29 71.7484 27.29ZM71.7484 25.318C73.2757 25.318 74.5227 25.0377 75.4894 24.477C76.4561 23.9163 77.1617 23.0463 77.6064 21.867C78.0704 20.6877 78.3024 19.1797 78.3024 17.343C78.3024 15.5063 78.0704 14.008 77.6064 12.848C77.1617 11.6687 76.4561 10.7987 75.4894 10.238C74.5227 9.658 73.2757 9.368 71.7484 9.368C70.2404 9.368 69.0031 9.658 68.0364 10.238C67.0697 10.7987 66.3544 11.6687 65.8904 12.848C65.4457 14.008 65.2234 15.5063 65.2234 17.343C65.2234 19.1797 65.4457 20.6877 65.8904 21.867C66.3544 23.0463 67.0697 23.9163 68.0364 24.477C69.0031 25.0377 70.2404 25.318 71.7484 25.318ZM72.9664 5.946V3.655H75.2574V5.946H72.9664ZM68.6744 5.946V3.655H70.9654V5.946H68.6744ZM84.6462 27V7.657H86.7342V24.97H96.6232V27H84.6462ZM107.395 27.29C104.998 27.29 103.132 26.5843 101.798 25.173C100.483 23.7617 99.8258 21.7413 99.8258 19.112V7.657H101.914V19.228C101.914 21.2967 102.378 22.8337 103.306 23.839C104.234 24.825 105.597 25.318 107.395 25.318C109.193 25.318 110.566 24.825 111.513 23.839C112.46 22.8337 112.934 21.2967 112.934 19.228V7.657H114.964V19.112C114.964 20.8713 114.664 22.36 114.065 23.578C113.466 24.796 112.605 25.724 111.484 26.362C110.363 26.9807 109 27.29 107.395 27.29ZM119.905 27V7.657H127.851C129.746 7.657 131.167 8.13067 132.114 9.078C133.081 10.0253 133.564 11.427 133.564 13.283C133.564 14.4043 133.332 15.4 132.868 16.27C132.404 17.14 131.756 17.8263 130.925 18.329C130.094 18.8317 129.117 19.083 127.996 19.083L128.866 18.503L134.434 27H131.969L126.227 18.126L127.271 19.199H121.993V27H119.905ZM121.993 17.227H126.894C128.499 17.227 129.659 16.908 130.374 16.27C131.109 15.6127 131.476 14.6653 131.476 13.428C131.476 12.268 131.205 11.3497 130.664 10.673C130.123 9.977 129.108 9.629 127.619 9.629H121.993V17.227Z"
					fill="white"
				/>
				<line
					x1="9.92859"
					y1="17.5"
					x2="141.929"
					y2="17.5"
					stroke="#B91C1C"
					strokeWidth="4"
				/>
			</svg>
		</Link>
	);
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>();
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	);
}

export default withSentry(AppWithProviders);

function UserDropdown() {
	const user = useUser();
	const submit = useSubmit();
	const formRef = useRef<HTMLFormElement>(null);
	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button asChild variant="outline">
					<Link
						to={"/app/company-settings"}
						// this is for progressive enhancement
						onClick={(e) => e.preventDefault()}
						className="flex items-center gap-2"
					>
						{user.company?.logo?.id ? (
							<img
								className="h-8 w-8 rounded-full object-cover"
								alt={`${user.company.name} logo` ?? "logo"}
								src={
									getCompanyImgSrc(user.company.logo?.id) ?? "/default-logo.svg"
								}
							/>
						) : (
							<div className="h-8 w-8 rounded-full bg-gray-200" />
						)}
						<div>
							{user.company?.name && (
								<div className="text-sm font-bold">{user.company?.name}</div>
							)}
							<span className="text-xs font-normal">{user.name}</span>
						</div>
					</Link>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownMenuContent sideOffset={8} align="start">
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to={"/app/sales"}>
							<Icon className="text-body-md" name="sun">
								Auglýsingar
							</Icon>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem>
						<Link prefetch="intent" to={`/users/${user.id}`}>
							<Icon className="text-body-md" name="avatar">
								Notandastillingar
							</Icon>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to={"/app/company-settings"}>
							<Icon className="text-body-md" name="avatar">
								Fyrirtækjastillingar
							</Icon>
						</Link>
					</DropdownMenuItem>
					<div className={"mt-2 pt-2 border-t"}>
						<DropdownMenuItem
							asChild
							// this prevents the menu from closing before the form submission is completed
							onSelect={(event) => {
								event.preventDefault();
								submit(formRef.current);
							}}
						>
							<Form action="/logout" method="POST" ref={formRef}>
								<Icon className="text-body-md" name="exit">
									<button type="submit">Útskráning</button>
								</Icon>
							</Form>
						</DropdownMenuItem>
					</div>
				</DropdownMenuContent>
			</DropdownMenuPortal>
		</DropdownMenu>
	);
}

/**
 * @returns the user's theme preference, or the client hint theme if the user
 * has not set a preference.
 */
export function useTheme() {
	const hints = useHints();
	const requestInfo = useRequestInfo();
	const optimisticMode = useOptimisticThemeMode();
	if (optimisticMode) {
		return optimisticMode === "system" ? hints.theme : optimisticMode;
	}
	return requestInfo.userPrefs.theme ?? hints.theme;
}

/**
 * If the user's changing their theme mode preference, this will return the
 * value it's being changed to.
 */
export function useOptimisticThemeMode() {
	const fetchers = useFetchers();
	const themeFetcher = fetchers.find((f) => f.formAction === "/");

	if (themeFetcher?.formData) {
		const submission = parseWithZod(themeFetcher.formData, {
			schema: ThemeFormSchema,
		});

		if (submission.status === "success") {
			return submission.value.theme;
		}
	}
}

/*function ThemeSwitch({ userPreference }: { userPreference?: Theme | null }) {
	const fetcher = useFetcher<typeof action>();

	const [form] = useForm({
		id: "theme-switch",
		lastResult: fetcher.data?.result,
	});

	const optimisticMode = useOptimisticThemeMode();
	const mode = optimisticMode ?? userPreference ?? "system";
	const nextMode =
		mode === "system" ? "light" : mode === "light" ? "dark" : "system";
	const modeLabel = {
		light: (
			<Icon name="sun">
				<span className="sr-only">Light</span>
			</Icon>
		),
		dark: (
			<Icon name="moon">
				<span className="sr-only">Dark</span>
			</Icon>
		),
		system: (
			<Icon name="laptop">
				<span className="sr-only">System</span>
			</Icon>
		),
	};

	return (
		<fetcher.Form method="POST" {...getFormProps(form)}>
			<input type="hidden" name="theme" value={nextMode} />
			<div className="flex gap-2">
				<button
					type="submit"
					className="flex h-8 w-8 cursor-pointer items-center justify-center"
				>
					{modeLabel[mode]}
				</button>
			</div>
		</fetcher.Form>
	);
}*/

export function ErrorBoundary() {
	// the nonce doesn't rely on the loader so we can access that
	const nonce = useNonce();

	// NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
	// likely failed to run so we have to do the best we can.
	// We could probably do better than this (it's possible the loader did run).
	// This would require a change in Remix.

	// Just make sure your root route never errors out and you'll always be able
	// to give the user a better UX.

	return (
		<Document nonce={nonce}>
			<GeneralErrorBoundary />
		</Document>
	);
}
